import React, { createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import { Line } from 'react-chartjs-2';
import ShowChart from '@material-ui/icons/ShowChart';
import translationPropType from '../../../proptypes/translationPropType';
import MultiSelect from '../MultiSelect/MultiSelect';
import graphKeysPropTypes from '../../../proptypes/graphKeysPropTypes';

const graphOptions = {
    tooltips: {
        mode: 'index',
        intersect: false,
    },
    animation: {
        duration: 0,
    },
    hover: {
        mode: 'average',
        intersect: true,
    },
    legend: {
        display: false,
    },
    scales: {
        yAxes: [{
            ticks: {
                beginAtZero: true,
                suggestedMax: 15,
            },
        }],
    },
};

const styles = theme => ({
    graphContainer: {
        marginTop: theme.spacing(3),
        position: 'relative',
        width: '100%',
    },
    selectsWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        '@media print': {
            display: 'none',
        },
    },
    grow: {
        flexGrow: 1,
    },
    browserGraph: {
        '@media print': {
            display: 'none',
        },
    },
    printGraph: {
        display: 'none',
        '@media print': {
            display: 'block',
        },
    },
});

const LineGraph = ({
    classes,
    graphData,
    graphKeys,
    selectedGraphKeys,
    isAdvancedUser,
    handleGroupBy,
    handleSelectGraphKeys,
    groupBy,
    translation,
}) => {
    let chartReference;

    const setChartRef = ref => chartReference = ref;

    const printMap = createRef();

    const clearPrintGraph = () => {
        const { current } = printMap || {};
        while (current && current.firstChild) {
            printMap.current.firstChild.remove();
        }
    };

    const drawPrintGraph = () => {
        clearPrintGraph();

        const { chartInstance } = chartReference || {};

        if (chartInstance) {
            const chartImg = chartReference.chartInstance.toBase64Image();
            const image = new Image();
            image.src = chartImg;
            image.style.maxWidth = '100%';
            printMap.current.appendChild(image);
        }
    };

    useEffect(() => {
        drawPrintGraph();
    });

    const group = (event) => {
        handleGroupBy(event.target.value);
    };

    const getData = () => {
        if (!graphKeys || !selectedGraphKeys) {
            return _.cloneDeep(graphData);
        }
        const newDataSets = graphData.datasets.map(dataSet => Object.assign({}, dataSet, {
            hidden: !selectedGraphKeys.find(key => key.id === dataSet.id),
        }));

        return Object.assign({}, graphData, {
            datasets: newDataSets,
        });
    };

    return (
        <div className={classes.graphContainer}>
            <div className={classes.selectsWrapper}>
                {isAdvancedUser
                && handleSelectGraphKeys
                && graphKeys
                && selectedGraphKeys
                && graphKeys.length > 1
                && graphKeys.length < 250
                && (
                    <MultiSelect
                        allValues={graphKeys}
                        selectedValues={selectedGraphKeys}
                        handleSelect={handleSelectGraphKeys}
                        label={translation.SHOW}
                        icon={<ShowChart />}
                    />
                )
                }
                <div className={classes.grow} />
                {isAdvancedUser && handleGroupBy && (
                    <FormControl className={classes.formControl}>
                        <InputLabel htmlFor="groudBy-simple">{translation.GROUP_BY}</InputLabel>
                        <Select
                            value={groupBy}
                            onChange={group}
                            inputProps={{
                                name: 'groupBy',
                                id: 'groudBy-simple',
                            }}
                        >
                            <MenuItem value="DAY">{translation.DAY}</MenuItem>
                            <MenuItem value="WEEK">{translation.WEEK}</MenuItem>
                            <MenuItem value="MONTH">{translation.MONTH}</MenuItem>
                        </Select>
                    </FormControl>
                )}
            </div>
            <div className={classes.browserGraph}>
                <Line
                    redraw
                    height={100}
                    ref={ref => setChartRef(ref)}
                    data={getData}
                    options={graphOptions}
                />
            </div>
            <div className={classes.printGraph} ref={printMap} />
        </div>
    );
};

LineGraph.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    graphData: PropTypes.objectOf(PropTypes.any).isRequired,
    handleGroupBy: PropTypes.func,
    isAdvancedUser: PropTypes.bool,
    groupBy: PropTypes.string,
    handleSelectGraphKeys: PropTypes.func,
    graphKeys: graphKeysPropTypes,
    selectedGraphKeys: graphKeysPropTypes,
};

LineGraph.defaultProps = {
    isAdvancedUser: false,
    handleGroupBy: null,
    handleSelectGraphKeys: null,
    selectedGraphKeys: null,
    graphKeys: null,
    groupBy: 'MONTH',
};

export default React.memo(withStyles(styles)(LineGraph));
